import { createUUID, RequestsBucket } from '@yandex-int/messenger.utils';

import { parentFrameCommunicator } from '../../../../../shared/features/parent-frame-communicator';
import { bind } from '../../../../../shared/helpers/decorators';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { IncomingEvents, IncomingPostMessage, OutgoingEvents } from '../../../iframe-sagas/types';
import { FileRecorder, FileRecorderCloseResult, FileRecorderOptions } from './types';

const logger = createLogger('recorder:pm');

export class PMRecorder implements FileRecorder {
    private requests = new RequestsBucket<string>();
    private options: FileRecorderOptions;

    constructor(options: FileRecorderOptions) {
        this.options = options;
    }

    @bind
    handlePostMessage(message: IncomingPostMessage) {
        switch (message.event) {
            case IncomingEvents.START_SCREEN_RECORDING_RESULT:
            case IncomingEvents.ADD_SCREEN_RECORDING_CHUNK_RESULT:
            case IncomingEvents.END_SCREEN_RECORDING_RESULT: {
                const { payload, error, reqId } = message;

                if (error) {
                    this.requests.result(reqId, error);
                } else {
                    this.requests.result(reqId, undefined, payload);
                }
            }
        }
    }

    request<R>(callback: (reqId: string) => void) {
        return this.requests.request<R>(() => {
            const reqId = createUUID();

            callback(reqId);

            return reqId;
        });
    }

    async init(): Promise<void> {
        return this.request<void>((reqId) => {
            parentFrameCommunicator.sendMessage(
                '*',
                {
                    event: OutgoingEvents.START_SCREEN_RECORDING,
                    reqId,
                    payload: {
                        mimeType: this.options.mimeType,
                        startTime: Date.now(),
                    },
                },
                false
            );
        });
    }

    addChunk(chunk: Blob) {
        return this.request<void>((reqId) => {
            parentFrameCommunicator.sendMessage(
                '*',
                {
                    event: OutgoingEvents.ADD_SCREEN_RECORDING_CHUNK,
                    reqId,
                    payload: {
                        chunk,
                        chunkDuration: this.options.chunkDuration,
                    },
                },
                false
            );
        });
    }

    async close(): Promise<FileRecorderCloseResult> {
        try {
            await this.request<void>((reqId) => {
                parentFrameCommunicator.sendMessage('*', {
                    event: OutgoingEvents.END_SCREEN_RECORDING,
                    reqId,
                });
            });

            return { status: 'saved' };
        } catch (error) {
            logger.error(error, {
                method: 'close',
            });
            throw error;
        }
    }
}
